import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Loading, Btn } from "@/components/primitives";
import { StatusBadge } from "@/components/StatusBadge";
import { CALL_STATUSES, fmtDate, statusMeta, TRACKING_STAGES, mapsDir } from "@/lib/format";
import { lastGpsFromTracking, destForCall, mapsFromTo } from "@/lib/routing";
import FormFillDialog from "@/components/FormFillDialog";
import { toast } from "sonner";
import {
  ArrowLeft, Pencil, Trash2, FileText, MapPin, CheckCircle2, Circle,
  Navigation, Loader2, ChevronRight, Truck, ClipboardList, ExternalLink,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const FLAGS = [["house_call", "House Call"], ["assist", "Assist"], ["ice", "ICE"], ["body_bag", "Body Bag"]];

export default function TransportDetail() {
  const { id } = useParams();
  const { effectiveIsAdmin } = useAuth();
  const [call, setCall] = useState(null);
  const [forms, setForms] = useState(null);       // {agency, required:[...]}
  const [schemas, setSchemas] = useState({});      // key -> schema[]
  const [newStatus, setNewStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeForm, setActiveForm] = useState(null); // {key,label,existing}
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get(`/calls/${id}`).then((r) => { setCall(r.data); setNewStatus(r.data.status); }).catch(() => setCall(false));
    api.get(`/calls/${id}/forms`).then((r) => setForms(r.data)).catch(() => setForms({ required: [] }));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/forms").then((r) => {
      const map = {};
      r.data.forEach((f) => { map[f.key] = f.schema; });
      setSchemas(map);
    }).catch(() => {});
  }, [load]);

  if (call === null) return <Loading />;
  if (call === false) return <Card className="p-8 text-center text-curtron-sub">Transport not found.</Card>;

  const updateStatus = async () => {
    if (newStatus === call.status) return;
    setBusy(true);
    try {
      await api.post(`/calls/${id}/status`, { status: newStatus });
      toast.success(`Status updated to ${statusMeta(newStatus).label}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const genRunSheet = async () => {
    setBusy(true);
    try {
      await api.post(`/calls/${id}/run-sheet`, {});
      toast.success("Run Sheet generated", { description: "Saved to Documents." });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const del = async () => {
    try { await api.delete(`/calls/${id}`); toast.success("Transport deleted"); navigate("/transports"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const tracking = call.tracking || {};
  const required = forms?.required || [];
  const completedCount = required.filter((f) => f.completed).length;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => navigate("/transports")} className="inline-flex items-center gap-1.5 text-sm text-curtron-sub hover:text-curtron-ink transition-colors" data-testid="back-to-transports">
          <ArrowLeft className="w-4 h-4" /> Transport Detail
        </button>
        {effectiveIsAdmin && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Btn variant="ghost" className="h-8 px-2 text-curtron-terracotta" data-testid="delete-transport-btn"><Trash2 className="w-4 h-4" /></Btn>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete transport for {call.deceased_name || "this record"}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the transport record for <span className="font-medium">{call.deceased_name || "—"}</span> ({call.agency_for || "—"}). This cannot be undone.
                  {call.status === "completed" && " ⚠️ This is a COMPLETED historical record — its run sheet and paperwork will be lost."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={del} data-testid="delete-confirm" className="bg-curtron-terracotta hover:bg-[#A35549]">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Header */}
      <div className="mb-4">
        <StatusBadge status={call.status} />
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-curtron-ink tracking-tight mt-2">{call.deceased_name || "Transport"}</h1>
        <p className="text-sm text-curtron-sub mt-0.5">
          {call.agency_for || "—"}{(call.assigned_to_name || call.driver_name) ? ` · Driver: ${call.assigned_to_name || call.driver_name}` : ""}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-4">
          {/* Route */}
          <Card className="p-4 sm:p-5">
            <h2 className="font-heading font-semibold text-curtron-ink mb-3">Route</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-curtron-moss/20 text-curtron-green flex items-center justify-center shrink-0"><MapPin className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-wide text-curtron-muted">From</div>
                  {call.pickup_facility_name && <div className="text-sm font-medium text-curtron-ink">{call.pickup_facility_name}</div>}
                  <div className="text-sm text-curtron-ink">{call.pickup_location || "—"}</div>
                  {call.pickup_city_state && <div className="text-sm text-curtron-sub">{call.pickup_city_state}</div>}
                  {call.pickup_location && (
                    <a href={mapsDir(call.pickup_location, call.pickup_city_state)} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-curtron-green inline-flex items-center gap-1 hover:underline mt-1" data-testid="dir-pickup">
                      <Navigation className="w-3 h-3" /> Directions <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-curtron-terracotta/15 text-curtron-terracotta flex items-center justify-center shrink-0"><MapPin className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-wide text-curtron-muted">To</div>
                  {call.dropoff_facility_name && <div className="text-sm font-medium text-curtron-ink">{call.dropoff_facility_name}</div>}
                  <div className="text-sm text-curtron-ink">{call.dropoff_location || "—"}</div>
                  {call.dropoff_city_state && <div className="text-sm text-curtron-sub">{call.dropoff_city_state}</div>}
                  {call.dropoff_location && (
                    <a href={mapsDir(call.dropoff_location, call.dropoff_city_state)} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-curtron-green inline-flex items-center gap-1 hover:underline mt-1" data-testid="dir-dropoff">
                      <Navigation className="w-3 h-3" /> Directions <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="text-xs text-curtron-muted mt-4 pt-3 border-t border-curtron-line">Scheduled {fmtDate(call.scheduled_at)}</div>
            {(() => {
              const gps = lastGpsFromTracking(tracking);
              const { address, label } = destForCall(call);
              if (!gps || !address) return null;
              return (
                <a href={mapsFromTo(gps, address)} target="_blank" rel="noopener noreferrer" className="block mt-3" data-testid="navigate-from-driver">
                  <Btn variant="secondary" className="w-full"><Navigation className="w-4 h-4" /> Navigate from driver&apos;s last GPS to {label}</Btn>
                </a>
              );
            })()}
          </Card>

          {/* Progress */}
          <Card className="p-4 sm:p-5">
            <h2 className="font-heading font-semibold text-curtron-ink mb-4">Progress</h2>
            <ol className="space-y-3">
              {TRACKING_STAGES.map((st) => {
                const at = tracking[st.key];
                const lat = tracking[st.key.replace("_at", "_lat")];
                const lng = tracking[st.key.replace("_at", "_lng")];
                const done = !!at;
                return (
                  <li key={st.key} className="flex items-start gap-3">
                    {done ? <CheckCircle2 className="w-5 h-5 text-curtron-green shrink-0" /> : <Circle className="w-5 h-5 text-curtron-line shrink-0" />}
                    <div className="min-w-0">
                      <div className={`text-sm font-medium ${done ? "text-curtron-ink" : "text-curtron-muted"}`}>{st.label}</div>
                      {done && <div className="text-xs text-curtron-sub">{fmtDate(at)}</div>}
                      {done && lat != null && lng != null && (
                        <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-curtron-green inline-flex items-center gap-1 hover:underline mt-0.5" data-testid={`map-link-${st.key}`}>
                          <MapPin className="w-3 h-3" /> {Number(lat).toFixed(4)}, {Number(lng).toFixed(4)}
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </Card>

          {/* Driver / Vehicle */}
          <Card className="p-4 sm:p-5">
            <h2 className="font-heading font-semibold text-curtron-ink mb-3">Driver / Vehicle</h2>
            <div className="grid grid-cols-2 gap-4">
              <div><div className="text-[11px] uppercase tracking-wide text-curtron-muted">Driver</div><div className="text-sm text-curtron-ink mt-0.5">{call.assigned_to_name || call.driver_name || "—"}</div></div>
              <div><div className="text-[11px] uppercase tracking-wide text-curtron-muted">Van #</div><div className="text-sm text-curtron-ink mt-0.5">{call.van_number || "—"}</div></div>
              {call.assigned_to_secondary_name && (
                <div><div className="text-[11px] uppercase tracking-wide text-curtron-muted">Back-up Driver</div><div className="text-sm text-curtron-ink mt-0.5">{call.assigned_to_secondary_name}</div></div>
              )}
            </div>
          </Card>

          {/* Driver Delivery Sheet */}
          <Card className="p-4 sm:p-5">
            <h2 className="font-heading font-semibold text-curtron-ink mb-3">Driver Delivery Sheet</h2>
            <div className="flex flex-wrap gap-2">
              {FLAGS.map(([k, l]) => (
                <span key={k} className={`text-xs rounded-md px-2 py-1 border ${call[k] ? "bg-curtron-green/10 text-curtron-green border-curtron-green/20" : "text-curtron-muted border-curtron-line"}`}>
                  {call[k] ? "✓ " : ""}{l}
                </span>
              ))}
            </div>
            <div className="mt-3 text-sm"><span className="text-curtron-muted">Belongings: </span><span className="text-curtron-ink">{call.personal_belongings || "N/A"}</span></div>
            {call.signature_b64 && (
              <img src={call.signature_b64.startsWith("data:") ? call.signature_b64 : `data:image/png;base64,${call.signature_b64}`} alt="signature" className="max-h-24 border border-curtron-line rounded-md bg-white mt-3" />
            )}
          </Card>

          {/* Run Sheet */}
          <Card className="p-4 sm:p-5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-heading font-semibold text-curtron-ink">View / Print Run Sheet</h2>
              <p className="text-sm text-curtron-sub mt-0.5">Run Sheet PDF is saved to Documents.</p>
            </div>
            <Btn variant="secondary" onClick={genRunSheet} disabled={busy} data-testid="gen-runsheet-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Generate
            </Btn>
          </Card>

          {/* Required Agency Forms */}
          <Card className="p-4 sm:p-5">
            <h2 className="font-heading font-semibold text-curtron-ink mb-3 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-curtron-green" /> Required Agency Forms {forms ? `(${completedCount}/${required.length})` : ""}
            </h2>
            {required.length === 0 ? (
              <p className="text-sm text-curtron-sub">No required forms for this agency.</p>
            ) : (
              <div className="divide-y divide-curtron-line -mx-1">
                {required.map((f) => (
                  <button key={f.key} onClick={() => setActiveForm({ key: f.key, label: f.label, existing: f.form_data })}
                    disabled={!schemas[f.key]} data-testid={`form-row-${f.key}`}
                    className="w-full flex items-center gap-3 px-1 py-3 text-left hover:bg-curtron-bg rounded-md transition-colors disabled:opacity-60">
                    {f.completed ? <CheckCircle2 className="w-5 h-5 text-curtron-green shrink-0" /> : <Circle className="w-5 h-5 text-curtron-line shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-curtron-ink">{f.label}</div>
                      <div className="text-xs text-curtron-muted">{f.completed ? `Completed ${fmtDate(f.completed_at)}` : "Tap to fill out"}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-curtron-muted shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="p-4 sm:p-5">
            <h2 className="font-heading font-semibold text-curtron-ink mb-3">Update Status</h2>
            <div className="flex gap-2">
              <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="status-select"
                className="flex-1 h-9 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green">
                {CALL_STATUSES.concat(["cancelled"]).map((s) => <option key={s} value={s}>{statusMeta(s).label}</option>)}
              </select>
              <Btn onClick={updateStatus} disabled={busy || newStatus === call.status} data-testid="update-status-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}
              </Btn>
            </div>
          </Card>

          {effectiveIsAdmin && <Btn onClick={() => navigate(`/transports/${id}/edit`)} data-testid="edit-transport-btn" className="w-full"><Pencil className="w-4 h-4" /> Edit &amp; regenerate PDF</Btn>}

          {call.notes && (
            <Card className="p-4 sm:p-5">
              <h2 className="font-heading font-semibold text-curtron-ink mb-2 flex items-center gap-2"><Truck className="w-4 h-4 text-curtron-green" /> Notes</h2>
              <p className="text-sm text-curtron-sub whitespace-pre-wrap">{call.notes}</p>
            </Card>
          )}
        </div>
      </div>

      {activeForm && (
        <FormFillDialog
          open={!!activeForm}
          onOpenChange={(v) => !v && setActiveForm(null)}
          callId={id}
          formKey={activeForm.key}
          label={activeForm.label}
          schema={schemas[activeForm.key]}
          existing={activeForm.existing}
          call={call}
          onSaved={load}
        />
      )}
    </div>
  );
}
